import React, { useContext } from 'react'
import styled from 'styled-components'
import Router, { useRouter } from 'next/router'

import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  borderRadius,
  borderRadiusSizes,
  buttonStyles,
  colorConstants,
  colors,
  header,
  iconSizes,
  iconSizesInPixel,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import modalController from 'src/decorators/ModalController'
import { H4, P, Text } from '@jarvis.oddle/core/components/Typo'
import Button from '@jarvis.oddle/core/components/Button'
import Dropdown from '@jarvis.oddle/core/components/Dropdown'
import { generateImageSources } from '@jarvis.oddle/core/tools/uploadcare'
import Card from '@jarvis.oddle/core/components/Card'
import IconArrow, {
  arrowDirections
} from '@jarvis.oddle/core/components/Icons/IconArrow'
import IconGlobe from '@jarvis.oddle/core/components/Icons/IconGlobe'
import { languages } from '@jarvis.oddle/core/tools/localisation'
import {
  lightenOrDarken,
  numberise,
  pixelise
} from '@jarvis.oddle/core/tools/style'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import { parseJwt } from 'tools/jwtParser'
import { DotComponent } from '@jarvis.oddle/core/components/Radio/Radio'
import { isEmpty } from 'src/utils/string'
import IconWarning from '@jarvis.oddle/core/components/Icons/IconWarning'
import useTimer from '@jarvis.oddle/core/hooks/useTimer'
import { logErrorToRollbar } from '@jarvis.oddle/core/tools/rollbarHelper'
import Divider from '@jarvis.oddle/core/components/Divider'
import IconShoppingBag from '@jarvis.oddle/core/components/Icons/IconShoppingBag'

import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import { getTranslatedWeekDays, formatDate } from 'src/utils/date'
import OrderingFlowContext from 'src/containers/OrderingFlowContext'
import { createUrl, redirect } from 'src/utils/routes'
import useAppSelector from 'src/hooks/useAppSelector'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { getTranslatedPagesArray } from 'src/components/molecules/UserPagesNavigation'
import VerticalNavigation from 'src/components/molecules/VerticalNavigation'
import {
  fetchUserAfterLogoutAction,
  fetchUserViaOddlePassAction,
  getAllAddressesOPAction,
  getAllSavedCardsAction,
  signUpAction
} from 'src/redux/user'
import {
  initializedOPWAction,
  selectDetailMenuSite,
  selectMenuMode
} from 'src/redux/meta'
import useTracker from 'src/hooks/useTracker'
import {
  getEligiblePromotionsAction,
  syncMethodAction
} from 'src/redux/order/action'
import { DesktopHidden, DesktopOnly } from 'src/components/core/Responsive'
import ResponsiveSection from 'src/components/core/ResponsiveSection'
import { selectOrder, syncPickupLocationAction } from 'src/redux/order'
import { selectStore } from 'src/redux/stores'
import ShopLink, { NextLink } from 'src/components/molecules/ShopLink'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { getOrderItemQuantity } from 'src/utils/product'
import { useAppContext } from 'src/containers/AppContext'
import UserContext from 'src/containers/UserContext'
import HeaderLogo from 'src/components/molecules/HeaderLogo'
import IconNewBag from 'src/components/icons/IconNewBag'
import Badge from 'src/components/molecules/Badge'
import Hamburger from 'src/components/molecules/Hamburger'
import IconNewCalendar from 'src/components/icons/IconNewCalendar'
import { ODDLE_PASS_CLIENT_ID, ODDLE_PASS_REDIRECT_URL } from 'config/client'
import BadgeWithAmount from 'src/components/molecules/BadgeWithAmount'
import IconRestaurant from 'src/components/icons/IconRestaurant'
import { newrelicIdentifier } from 'src/utils/newrelicLogger'

const LinkText = styled(H4)`
  color: ${(props) => props.color || colorConstants.white};
  margin: 0 ${margin[marginSizes.small]};
  cursor: pointer;

  &:hover {
    opacity: ${header.linkOpacityOnHover};
  }
`

const NavContainer = styled(DesktopOnly)`
  flex-grow: 1;
  flex-shrink: 0;
`

const StyledResponsiveSection = styled(ResponsiveSection)`
  margin-top: 0;
  margin-bottom: 0;
  height: ${(props) =>
    pixelise(props.mobileHeight === 198 ? 220 : props.mobileHeight)};
  background-color: ${(props) => props.backgroundColor || colors.brandPrimary};
  position: sticky;
  top: 0;
  z-index: 3;

  ${breakpoint('desktop')`
    height: ${(props) => pixelise(props.desktopHeight)};
  `}
  .iPhoneX & {
    height: calc(
      ${(props) => pixelise(props.mobileHeight)} + env(safe-area-inset-top)
    );
    padding-top: env(safe-area-inset-top);
  }
`

const StyledResponsiveSectionContainer = styled(ResponsiveSection.Container)`
  max-width: calc(100vw - 20px);
  padding-left: 0;
  padding-right: 0;
  display: block;
  @media (max-width: 1023px) {
    transition: all 0.5s ease-in-out;
    transform: translateY(-50%);
    ${(props) =>
      props.scrollPosition <= 0
        ? `
      opacity: 1;
      transform: translateY(0);
    `
        : `
      opacity: 0;
      height: 0;
    `}
  }
`

const HeaderButton = styled(Button).attrs(() => ({
  buttonStyle: buttonStyles.solid
}))`
  border: none;
  margin-right: ${margin[marginSizes.medium]};
  margin-left: ${margin[marginSizes.small]};
`

const HeaderButtonText = styled(H4).attrs(() => ({
  normalised: true,
  marginLeft: marginSizes.none,
  bold: true
}))`
  color: inherit;
`

const DropdownContainer = styled(({ onClosed, ...props }) => (
  <Div {...props} />
))`
  position: absolute;
  top: calc(100% + 5px);
  width: 100%;
  z-index: 100;
  right: ${header.dropdownMenuOffset};
`

const NotifCloseToExpringContainerMobile = styled.div<{
  backgroundColor: string
}>`
  margin-top: 0;
  margin-bottom: 0;
  padding: 0;
  background-color: ${(props) => props.backgroundColor};
  width: 100%;
`

const NotifCloseToExpiringContainerMobileInside = styled(
  ResponsiveSection.Container
)`
  width: 100%;
  height: ${(props) =>
    props.mobileHeight === 198 ? pixelise(138) : pixelise(62)};
  transition: all 0.3s ease-out;
  padding-top: 20px;
  box-sizing: border-box;
`

const NotifCloseToExpringContainerDesktopOutside = styled.div<{
  backgroundColor: string
}>`
  width: 100%;
  background-color: ${(props) => props.backgroundColor};
  margin-top: 0;
  margin-bottom: 0;
  padding: 0;
  height: ${pixelise(62)};
  display: flex;
  justify-content: center;
`

const NotifCloseToExpringContainerDesktopInside = styled(
  ResponsiveSection.Container
)`
  display: flex;
  padding: 0;
  margin: 0;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`

const NotifCloseToExpringTextDesktop = styled(Text)`
  ${breakpoint('desktop')`
      text-align: center;
      width: 100%;
  `}
`

const NotifCloseToExpringTextMobile = styled(Text)`
  padding-right: 10px;
  font-size: 16px;
  ${(props) =>
    props.mobileHeight !== 198 &&
    `
    text-overflow: ellipsis;
    clear: both;
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    width: 73vw;
    padding-right: 0;
  `}
`

const NotifCloseToExpringContainer = styled.div`
  box-sizing: border-box;
`
const Span = styled.span`
  position: relative;
  top: 4px;
  margin-right: 10px;
`
const NotifCloseToExpringLinkTextDesktop = styled(LinkText)`
  text-decoration: underline;
  cursor: pointer;
  color: #000;
`
const NotifCloseToExpringLinkTextMobile = styled(LinkText)`
  text-decoration: underline;
  cursor: pointer;
  color: #000;
`

// WORKAROUND: VerticalNavigation is used a lot, use css selector to reduce side effects
// TODO: remove h4 inside a (H4 inside Link)
const UserVerticalNavigation = styled(VerticalNavigation)`
  background-color: ${(props) => props.theme.colorContainerColor};
  border-radius: ${borderRadius[borderRadiusSizes.medium]};
  padding: 0;
  width: 240px;

  & > a > div:hover {
    background-color: ${(props) =>
      lightenOrDarken(props.theme.colorContainerColor, 10)};
    border-radius: ${borderRadius[borderRadiusSizes.medium]};

    & > h4 {
      color: ${(props) => props.theme.buttonColor};
    }
  }
`

const CartDrawerButtonContainer = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center'
}))`
  height: 100%;
  cursor: pointer;
  ${(props) =>
    props.itemsCount > 0
      ? `
    position: relative;
    right: ${pixelise(numberise(iconSizesInPixel[props.iconSize]) / 2 + 5)};
  `
      : ''} /* Increase hit area */ padding: 0 ${padding[paddingSizes.small]};
  margin-right: -${padding[paddingSizes.extraSmall]};
`

const StyledStickyReservation = styled(ResponsiveSection)<{
  stuck: boolean
  scrollPosition: number
}>`
  width: 100%;
  z-index: 3;
  height: 48px;
  background-color: ${(props) => props.backgroundColor || colors.brandPrimary};
  flex-direction: row;
  justify-content: center;
  align-items: center;
  display: flex;
  margin: 0 auto;
  transition: all 0.5s ease-in-out;
  transform: translateY(-50%);

  ${(props) =>
    props.scrollPosition > 0
      ? `
    opacity: 1;
    transform: translateY(0);
  `
      : `
    opacity: 0;
    height: 0;
  `}
  ${breakpoint('desktop')`
    display: none;
  `}
  .iPhoneX & {
    padding-top: env(safe-area-inset-top);
  }
`

const StyledStickyReservationButton = styled(Flex)`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0;
  width: 49%;
`

const StyledStickyReservationText = styled(H4)`
  margin: 5px 5px 0 15px;
`

const StyledDivider = styled(Divider)`
  width: 2px;
  height: 24px;
  border-top: none;
  border-left: 1px solid
    ${(props) => props.color || props.theme.buttonTextColor};
`

const StyledBadge = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center'
}))`
  width: 24px;
  height: 24px;
  border-radius: 13px;
  background-color: ${(props) =>
    props.background ? props.background : colors.headerBackground};
  margin: 0 10px 0 3px;
`

const StyledText = styled(P).attrs((props) => ({
  textAlign: 'center',
  color: props.color ? props.color : colors.headerNav,
  fontSize: '14px'
}))``

const LanguageCard = styled(Card)`
  position: absolute;
  z-index: 100;
  width: 140px;

  && {
    padding: 0;
  }
`

const LanguageDropdownLabel = styled(Flex).attrs(() => ({
  role: 'button',
  alignItems: 'center',
  marginRight: marginSizes.small,
  marginLeft: marginSizes.small
}))`
  cursor: pointer;
`

const LanguageDropdownDot = styled(DotComponent)`
  margin-right: ${margin[marginSizes.small]};
  height: 18px;
  width: 18px;
`

// Note: LanguageDropdownDot is the div with color modified by border-color
const LanguageLinkBox = styled(Flex)`
  align-items: baseline;
  width: 100%;
  padding: 15px ${padding[paddingSizes.medium]};
  color: ${(props) => props.theme.colorHeadingsFont};
  border-radius: ${borderRadius[borderRadiusSizes.medium]};
  background-color: ${(props) => props.theme.colorContainerColor};

  & > div {
    border-color: ${(props) => props.theme.colorHeadingsFont};
  }

  &:hover {
    color: ${(props) => props.theme.buttonColor};
    background-color: ${(props) =>
      lightenOrDarken(props.theme.colorContainerColor, 10)};

    & > div {
      border-color: ${(props) => props.theme.buttonColor};
    }
  }
`

interface UserButtonProps {
  locale: string
  user: UserState
  buttonColor: string
  buttonTextColor: string
  initializedOPW: boolean
}

const UserButton = ({
  locale,
  user,
  buttonColor,
  buttonTextColor,
  initializedOPW
}: UserButtonProps) => {
  const { t } = useContext(TranslationContext)
  const { logout, login } = useContext(UserContext)

  return (
    <Flex alignItems='center'>
      {user.status === 'fulfilled' ? (
        <Dropdown
          renderLabel={({ toggleDropdown }) => (
            <HeaderButton
              buttonColor={buttonColor}
              buttonTextColor={buttonTextColor}
              onClick={toggleDropdown}
            >
              {t('Hi, _name_', { name: user.data.firstName })}
            </HeaderButton>
          )}
          renderDropdown={({ isDropdownOpening, closeDropdown }) =>
            isDropdownOpening && (
              <UserVerticalNavigation
                items={[
                  ...getTranslatedPagesArray(t, locale),
                  { id: 'logout', name: t('Log out'), href: null }
                ]}
                onClickItem={async (e, id) => {
                  if (id === 'logout') {
                    e.preventDefault()
                    await logout({ onSuccess: closeDropdown })
                  }
                }}
              />
            )
          }
          DropdownContainer={(props) => <DropdownContainer {...props} />}
        />
      ) : (
        <HeaderButton
          buttonColor={buttonColor}
          buttonTextColor={buttonTextColor}
          onClick={initializedOPW ? login : () => null}
          loading={
            user.status === 'pending' ||
            ![
              'customer_require_verification',
              'user_not_logged_in',
              'login_credentials_invalid',
              'email_banned',
              'login_credentials_not_found'
            ].includes(user.error)
          }
          ButtonText={() => <HeaderButtonText>{t('Sign In')}</HeaderButtonText>}
        />
      )}
    </Flex>
  )
}

interface HeaderMenuProps {
  color: string
  locale: string
  locales: string[]
  showStoreInfoLink: boolean
  enableReservationsOnShop: boolean
}

const HeaderMenu = ({
  color,
  showStoreInfoLink,
  locale,
  locales,
  enableReservationsOnShop
}: HeaderMenuProps) => {
  const router = useRouter()
  const { t } = useContext(TranslationContext)
  const directToProductSection = () => {
    // Timeout is set to 100ms as there's a browser issue in Firefox which causes automated regression tests unable to display the product section intermittently
    setTimeout(() => {
      const navigateToProductSection =
        document.getElementById('product-section')
      navigateToProductSection && navigateToProductSection.scrollIntoView(true)
    }, 100)
  }

  return (
    <Flex alignItems='center' flexDirection='row'>
      <Div>
        <NextLink
          href='/[locale]'
          as={`/${locale}`}
          passHref
          shallow={router.pathname === '/[locale]'}
        >
          <LinkText color={color} onClick={directToProductSection}>
            {t('Order')}
          </LinkText>
        </NextLink>
      </Div>
      {enableReservationsOnShop && (
        <NextLink
          href='/[locale]/reservations'
          as={`/${locale}/reservations`}
          passHref
        >
          <LinkText color={color}>{t('Reservations')}</LinkText>
        </NextLink>
      )}
      {showStoreInfoLink && (
        <NextLink href='/[locale]/stores' as={`/${locale}/stores`} passHref>
          <LinkText color={color}>{t('Stores')}</LinkText>
        </NextLink>
      )}
      {locales && locales.length > 1 && (
        <Div>
          <Dropdown
            renderLabel={({ toggleDropdown }) => (
              <LanguageDropdownLabel onClick={toggleDropdown}>
                <IconGlobe iconSize='medium' fill={color} />
                <LinkText color={color}>{languages[locale]}</LinkText>
                <IconArrow
                  iconSize='small'
                  fill={color}
                  direction={arrowDirections.down}
                />
              </LanguageDropdownLabel>
            )}
            renderDropdown={({ isDropdownOpening, closeDropdown }) =>
              isDropdownOpening && (
                <LanguageCard>
                  {locales.map((localeOption, key) => (
                    <ShopLink
                      key={key}
                      locale={localeOption}
                      onClick={closeDropdown}
                    >
                      <LanguageLinkBox>
                        <LanguageDropdownDot
                          size='small'
                          checked={locale === localeOption}
                        />
                        <H4 color='inherit' bold normalised>
                          {languages[localeOption]}
                        </H4>
                      </LanguageLinkBox>
                    </ShopLink>
                  ))}
                </LanguageCard>
              )
            }
          />
        </Div>
      )}
    </Flex>
  )
}

const Header = (): JSX.Element => {
  const router = useRouter()
  const locale = router.query.locale as string
  const { t } = useContext(TranslationContext)
  const dispatch = useAppDispatch()
  const {
    notifyLogin,
    segmentTrackerSignedOut,
    segmentTrackerSignedIn,
    notifyAccountCompleteRegistration
  } = useTracker()

  const userState = useAppSelector((state) => state['@user']).user
  const metaState = useAppSelector(selectDetailMenuSite)
  const orderState = useAppSelector(selectOrder)
  const storeState = useAppSelector(selectStore)
  const menuMode = useAppSelector(selectMenuMode)
  const initializedOPW = useAppSelector(
    (state) => state['@meta'].initializedOPW
  )
  const { previewMode, sectionSettingData, themeSettingData } = useAppSelector(
    (state) => state['@customisation'].data
  )
  const [scrollPosition, setScrollPosition] = React.useState(0)

  const {
    prepareToCheckout,
    redirectToCheckout,
    formatMoney,
    getLogoImagePath
  } = useAppContext()

  const itemsCount = orderState.storeOrder.data.orderItems
    ? getOrderItemQuantity(orderState.storeOrder.data.orderItems)
    : 0
  const { settings: customisationSettings = {}, id: sectionId } =
    sectionSettingData?.sections?.['header'] || {}
  const {
    showLogo,
    logoImage,
    homepageUrl: _homepageUrl,
    showStoreName = true,
    storeName,
    headerBackgroundColor,
    navigationLinkFontColor,
    storeNameFontType
  } = customisationSettings
  const homepageUrl = createUrl(_homepageUrl)
  const name = !isEmpty(storeName)
    ? storeName
    : !isEmpty(metaState.menu.name)
    ? metaState.menu.name
    : metaState.menu.name
  const stores = storeState.data.filter((s) => s.showAddressOnCart)
  const showStoreInfoLink = stores.length > 0
  const {
    locales,
    oddlePassEnabled,
    newOddlePassFlow,
    menuCountry,
    countryCode,
    features: { enableAccountLevelMarketingSubscription }
  } = metaState
  const {
    isEnableOcaAddress,
    isEnabledCustomerSaveCreditCardOnStripe,
    isOddlePassEnabled,
    isNewOddlePassEnabled
  } = useContext(UserContext)
  const {
    mobileHeight,
    desktopHeight,
    isCountdownTimeStart,
    setMobileHeight,
    setShowPreferredDateModal,
    setDesktopHeight,
    durationInMs,
    showPreferredDateModal
  } = useContext(OrderingFlowContext)
  const { isRunning, timeLeft, setTimeLeft, start } = useTimer(
    durationInMs,
    {},
    () => {
      if (!showPreferredDateModal) {
        setDesktopHeight(80)
        setMobileHeight(60)
        setShowPreferredDateModal(true)
      }
    }
  )
  const {
    isPickup,
    storeAddress,
    deliveryAddress,
    defaultStoreId,
    collectionTime,
    collectionDate
  } = orderState.storeOrder.data
  const translatedDaysOfWeek = getTranslatedWeekDays(t, 'long')
  const translatedCollectionDate =
    translatedDaysOfWeek[new Date(collectionDate).getDay()]

  const { settings: headerSettings = {} } =
    sectionSettingData?.sections?.['header'] || {}
  const storeOrder = useAppSelector(selectOrder).storeOrder

  React.useEffect(() => {
    if (
      window.opw &&
      metaState.menu &&
      !isEmpty(menuCountry) &&
      !initializedOPW
    ) {
      window.opw.initializeApp(
        ODDLE_PASS_CLIENT_ID,
        ODDLE_PASS_REDIRECT_URL,
        (router.query.locale as string) || 'en_SG',
        {
          menuName: name,
          state: {
            country: menuCountry
          },
          countryCode,
          disableMarketingMenu: false,
          useIframe: true,
          enabledAccMarketingSub: enableAccountLevelMarketingSubscription
        }
      )
    }
  }, [
    countryCode,
    menuCountry,
    metaState.menu,
    name,
    router.query.locale,
    initializedOPW,
    enableAccountLevelMarketingSubscription
  ])

  React.useEffect(() => {
    async function signPickupOrder() {
      await dispatch(syncMethodAction({ method: 'pickup' }))
      const storeId = (router.query.storeId ||
        router.query['store-id']) as string
      if (storeId) {
        await dispatch(
          syncPickupLocationAction({
            storeId,
            lat: null,
            long: null,
            selectedDate: null
          })
        )
        pickUpDeliveryFlow.send('dateTimeSelection', {
          storeId
        })
        return
      }
      pickUpDeliveryFlow.send('pickupStoreSelection')
    }

    async function signDeliveryOrder() {
      await dispatch(syncMethodAction({ method: 'delivery' }))
      pickUpDeliveryFlow.send('deliverySelectAddress')
    }

    // need to wait 1 second to make sure all updating for new draft store order has been finished
    // if not, it will throw error thrown.cause.extendedStackTrace:org.hibernate.StaleStateException: Batch update returned unexpected row count from update
    setTimeout(() => {
      if (router.query.selectDiningPreference === 'pickup') {
        signPickupOrder()
      }

      if (router.query.selectDiningPreference === 'delivery') {
        signDeliveryOrder()
      }
    }, 1000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchDataFromOddlePass = React.useCallback(async () => {
    if (
      typeof window !== 'undefined' &&
      window.localStorage.getItem('accessToken')
    ) {
      if (isEnableOcaAddress && isOddlePassEnabled && isNewOddlePassEnabled) {
        await dispatch(getAllAddressesOPAction())
      }
      if (isEnabledCustomerSaveCreditCardOnStripe) {
        await dispatch(getAllSavedCardsAction())
      }
    }
  }, [
    dispatch,
    isEnableOcaAddress,
    isEnabledCustomerSaveCreditCardOnStripe,
    isNewOddlePassEnabled,
    isOddlePassEnabled
  ])

  const goToPromotionOrCheckoutStep = React.useCallback(async () => {
    const response = await dispatch(getEligiblePromotionsAction())
    if (response.meta.requestStatus !== 'fulfilled') {
      logErrorToRollbar(
        'Error fetching eligible promotions',
        response.payload.error
      )
    }

    const { promoCodeEnabled, promotions } = response?.payload?.payload || {}
    if (promoCodeEnabled || (promotions || []).length > 0) {
      return modalController.showModal('CheckoutPromotionModal')
    }
    await prepareToCheckout({
      onReadyToCheckout: (storeOrder) => redirectToCheckout(storeOrder.id),
      onNotReadyToCheckout: (errors, errorCodes) =>
        modalController.showModal('CartErrorModal', { errors, errorCodes }),
      onShouldRefresh: () => undefined
    })
  }, [dispatch, prepareToCheckout, redirectToCheckout])

  React.useEffect(() => {
    if (window.opw && oddlePassEnabled && newOddlePassFlow) {
      window.opw.addListener(async (action, message) => {
        let userData
        switch (action) {
          case 'initialized':
            dispatch(initializedOPWAction())
            break
          case 'signin_success':
          case 'facebook_signin_success':
          case 'signup_success':
            userData = await dispatch(fetchUserViaOddlePassAction())
            await fetchDataFromOddlePass()
            if (
              userData.meta.requestStatus === 'fulfilled' &&
              userData.payload.payload.id
            ) {
              const parsedToken = parseJwt(
                window.localStorage.getItem('accessToken')
              )
              const continueWithOddlePass =
                (parsedToken.state || {}).continueWithOddlePass || false
              const authMethod = (parsedToken.state || {}).authMethod
              const {
                ocaId,
                firstName,
                lastName,
                email,
                phone,
                hasVerifiedPhone
              } = userData.payload.payload
              newrelicIdentifier({
                ocaId,
                email,
                phone
              })
              segmentTrackerSignedIn({
                id: ocaId,
                firstName,
                lastName,
                email,
                phone,
                continueWithOddlePass,
                country: menuCountry,
                phoneIsVerified: hasVerifiedPhone,
                authMethod
              })
              showNotificationToast.success(message)
              notifyLogin(authMethod)
              if ((parsedToken.state || {}).goToPromotionOrCheckoutStep) {
                return goToPromotionOrCheckoutStep()
              }
              break
            }
            if (menuMode === 'preview') {
              showNotificationToast.error(t('Cannot login on preview mode'))
              break
            }
            showNotificationToast.error(
              t(userData.payload.error || userData.payload.errorCode)
            )
            break
          case 'signout_success':
            window.localStorage.removeItem('accessToken')
            showNotificationToast.success(t('Successfully logged out.'))
            segmentTrackerSignedOut({ country: menuCountry })
            await dispatch(fetchUserAfterLogoutAction())
            await fetchDataFromOddlePass()
            window.location.reload()
            break
          case 'verification_success':
            userData = await dispatch(fetchUserViaOddlePassAction())
            const parsedToken = parseJwt(
              window.localStorage.getItem('accessToken')
            )
            // this logic make sure just process if browser not support 3rd party cookies, so OddlePass just can be use by redirect flow
            // after customer verified their information, OddlePass will redirect back to OddleShop and then should handle error customer_require_verification to call signUpAction
            if (
              userData.payload.payload.error ===
                'customer_require_verification' &&
              (parsedToken.state || {}).action === 'verification_success'
            ) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              const signUpResponse = await dispatch(
                signUpAction(parsedToken.state.data)
              )
              if (
                signUpResponse.meta.requestStatus === 'fulfilled' &&
                userData.payload.payload.id
              ) {
                notifyAccountCompleteRegistration(signUpResponse.payload)
              }
              userData = await dispatch(fetchUserViaOddlePassAction())
            }
            if (
              userData.meta.requestStatus === 'fulfilled' &&
              userData.payload.payload.id
            ) {
              showNotificationToast.success((parsedToken.state || {}).message)
            }
            if (userData.payload.payload.error) {
              if (menuMode === 'preview') {
                showNotificationToast.error(t('Cannot login on preview mode'))
                return
              }
              showNotificationToast.error(
                t(userData.payload.payload.error || userData.payload.errorCode)
              )
              return
            }
            await fetchDataFromOddlePass()
            if ((parsedToken.state || {}).goToPromotionOrCheckoutStep) {
              return goToPromotionOrCheckoutStep()
            }
            if (
              ((parsedToken.state || {}).configsCallback || {})
                .redirectToCheckoutPage
            ) {
              const {
                ocaId,
                firstName,
                lastName,
                email,
                phone,
                hasVerifiedPhone
              } = userData.payload.payload
              newrelicIdentifier({
                ocaId,
                email,
                phone
              })
              segmentTrackerSignedIn({
                id: ocaId,
                firstName,
                lastName,
                email,
                phone,
                country: menuCountry,
                continueWithOddlePass: false,
                phoneIsVerified: hasVerifiedPhone,
                authMethod: (parsedToken.state || {}).authMethod
              })
              redirect({
                url: parsedToken.state.configsCallback.path,
                as: parsedToken.state.configsCallback.as
              })
            }
            break
          default:
            break
        }
      })
    }
  }, [
    countryCode,
    dispatch,
    fetchDataFromOddlePass,
    goToPromotionOrCheckoutStep,
    menuCountry,
    menuMode,
    name,
    newOddlePassFlow,
    notifyAccountCompleteRegistration,
    notifyLogin,
    oddlePassEnabled,
    segmentTrackerSignedIn,
    segmentTrackerSignedOut,
    t,
    userState.status
  ])

  React.useEffect(() => {
    setTimeLeft(durationInMs)
  }, [durationInMs, setTimeLeft])

  React.useEffect(() => {
    if (isCountdownTimeStart) {
      start()
    }
  }, [isCountdownTimeStart, start])

  React.useEffect(() => {
    if (showPreferredDateModal) {
      modalController.showModal('ReminderOrderModal')
    }
  }, [showPreferredDateModal])

  const handleClickChange = () => {
    pickUpDeliveryFlow.start({
      onFinished: () => modalController.showModal('CartDrawer')
    })
  }

  const logoUrl = logoImage || getLogoImagePath()
  const { src, srcSet } = generateImageSources({
    url: logoUrl,
    baseW: 60
  })

  React.useEffect(() => {
    let shouldShowStickyBar = window.scrollY > 0

    const updateScrollDirection = () => {
      if (!shouldShowStickyBar && window.scrollY > 0) {
        setScrollPosition(window.scrollY)
        shouldShowStickyBar = true
      } else if (shouldShowStickyBar && window.scrollY === 0) {
        setScrollPosition(window.scrollY)
        shouldShowStickyBar = false
      }
    }

    window.addEventListener('scroll', updateScrollDirection)

    return () => window.removeEventListener('scroll', updateScrollDirection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isAppointmentReady =
    (isPickup && !!storeAddress && !defaultStoreId) ||
    (!isPickup && !!deliveryAddress)

  return (
    <StyledResponsiveSection
      backgroundColor={headerBackgroundColor}
      desktopHeight={desktopHeight}
      mobileHeight={mobileHeight}
      id='oddle-shop-header'
    >
      {/* Notification  */}
      {timeLeft > 0 && isRunning && collectionTime && (
        <React.Fragment>
          {/* mobile */}
          <DesktopHidden>
            <NotifCloseToExpringContainerMobile
              backgroundColor={themeSettingData.settings.colorContainerColor}
            >
              <NotifCloseToExpiringContainerMobileInside
                mobileHeight={mobileHeight}
              >
                <Flex flexDirection='column'>
                  <Flex justifyContent='space-between'>
                    <Flex
                      alignItems={
                        mobileHeight === 198 ? 'flex-start' : 'center'
                      }
                    >
                      <Span>
                        <IconWarning />
                      </Span>
                      {/* collapsible */}
                      <NotifCloseToExpringTextMobile
                        mobileHeight={mobileHeight}
                      >
                        {t(
                          'Complete this order in _minutes_:_seconds_  minutes to get your order delivered by _day_, _date_, _timeOne_ to _timeTwo_',
                          {
                            minutes: Math.floor((timeLeft * 0.001) / 60)
                              .toString(10)
                              .padStart(2, '0'),
                            seconds: Math.floor((timeLeft * 0.001) % 60)
                              .toString(10)
                              .padStart(2, '0'),
                            day: translatedCollectionDate,
                            date: formatDate({
                              t,
                              date: collectionDate,
                              locale: router.query.locale as string,
                              options: {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              }
                            }),
                            timeOne: collectionTime.split('to')[0],
                            timeTwo: collectionTime.split('to')[1]
                          }
                        )}
                      </NotifCloseToExpringTextMobile>
                    </Flex>
                    {mobileHeight === 198 ? (
                      <Div onClick={() => setMobileHeight(135)}>
                        <IconArrow.Up iconSize='small' />
                      </Div>
                    ) : (
                      <Div onClick={() => setMobileHeight(198)}>
                        <IconArrow.Down iconSize='small' />
                      </Div>
                    )}
                  </Flex>
                  {mobileHeight === 198 && (
                    <Flex justifyContent='flex-end'>
                      <NotifCloseToExpringLinkTextMobile
                        onClick={handleClickChange}
                      >
                        Change Time
                      </NotifCloseToExpringLinkTextMobile>
                    </Flex>
                  )}
                </Flex>
              </NotifCloseToExpiringContainerMobileInside>
            </NotifCloseToExpringContainerMobile>
          </DesktopHidden>
          {/* Desktop */}
          <DesktopOnly>
            <NotifCloseToExpringContainerDesktopOutside
              backgroundColor={themeSettingData.settings.colorContainerColor}
            >
              <NotifCloseToExpringContainerDesktopInside>
                <NotifCloseToExpringContainer>
                  <Span>
                    <IconWarning />
                  </Span>
                  <NotifCloseToExpringTextDesktop>
                    {t(
                      'Complete this order in _minutes_:_seconds_  minutes to get your order delivered by _day_, _date_, _timeOne_ to _timeTwo_',
                      {
                        minutes: Math.floor((timeLeft * 0.001) / 60)
                          .toString(10)
                          .padStart(2, '0'),
                        seconds: Math.floor((timeLeft * 0.001) % 60)
                          .toString(10)
                          .padStart(2, '0'),
                        day: translatedCollectionDate,
                        date: formatDate({
                          t,
                          date: collectionDate,
                          locale: router.query.locale as string,
                          options: {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }
                        }),
                        timeOne: collectionTime.split('to')[0],
                        timeTwo: collectionTime.split('to')[1]
                      }
                    )}
                  </NotifCloseToExpringTextDesktop>
                </NotifCloseToExpringContainer>
                <NotifCloseToExpringLinkTextDesktop onClick={handleClickChange}>
                  Change Time
                </NotifCloseToExpringLinkTextDesktop>
              </NotifCloseToExpringContainerDesktopInside>
            </NotifCloseToExpringContainerDesktopOutside>
          </DesktopOnly>
        </React.Fragment>
      )}
      <StyledStickyReservation
        backgroundColor={headerSettings?.headerBackgroundColor}
        scrollPosition={scrollPosition}
      >
        {metaState?.channelEnabled?.reserve ? (
          <>
            <StyledStickyReservationButton
              onClick={() =>
                router.push(`/${router.query.locale}/reservations`)
              }
            >
              <IconNewCalendar
                width={19}
                height={19}
                fill={headerSettings?.navigationLinkFontColor}
              />
              <StyledStickyReservationText
                color={headerSettings?.navigationLinkFontColor}
              >
                {t('Find a table')}
              </StyledStickyReservationText>
            </StyledStickyReservationButton>
            <StyledDivider />
          </>
        ) : null}
        <StyledStickyReservationButton
          onClick={() => {
            if (itemsCount > 0) {
              modalController.showModal('CartDrawer')
              return
            }
            const action = () => {
              const navigateToProductSection = document.getElementById(
                'product-section-mobile'
              )
              navigateToProductSection &&
                navigateToProductSection.scrollIntoView(true)
            }
            if (router.pathname === '/[locale]') {
              action()
              return
            }
            Router.push('/[locale]', `/${locale}`).then(() => action)
          }}
        >
          <BadgeWithAmount
            Icon={itemsCount > 0 ? IconShoppingBag : IconRestaurant}
            iconSize={iconSizes.medium}
            iconFillColor={headerSettings?.navigationLinkFontColor}
            value={itemsCount}
            badgeBackgroundColor={headerSettings?.navigationLinkFontColor}
            badgeTextColor={headerSettings?.headerBackgroundColor}
            totalAmount={formatMoney(storeOrder.data.total)}
            withAnimation
          />
        </StyledStickyReservationButton>
      </StyledStickyReservation>
      <StyledResponsiveSectionContainer
        height={isRunning ? '60px' : '100%'}
        scrollPosition={scrollPosition}
      >
        <Flex height='100%' alignItems='center' justifyContent='space-between'>
          <a
            onClick={(e) => {
              if (!previewMode) {
                window.location.href = homepageUrl
                return
              }
              e.preventDefault()
              window.confirm(
                t(
                  'This link cannot be opened in customisation preview. It will be opened in a new window (_target_).\nClick OK to continue',
                  { target: homepageUrl }
                )
              ) && window.open(homepageUrl, '_blank')
            }}
          >
            <DesktopOnly>
              <Flex justifyContent='center' alignItems='center'>
                <HeaderLogo
                  logoImage={logoImage}
                  showLogo={showLogo}
                  showStoreName={showStoreName}
                  storeName={name}
                  font={storeNameFontType}
                  textColor={navigationLinkFontColor}
                />
              </Flex>
            </DesktopOnly>
            <DesktopHidden>
              <HeaderLogo
                logoImage={logoImage}
                showLogo={showLogo}
                showStoreName={showStoreName}
                storeName={name}
                font={storeNameFontType}
                textColor={navigationLinkFontColor}
                isMobile
              />
            </DesktopHidden>
          </a>
          <Flex alignItems='center' justifyContent='space-between'>
            <NavContainer>
              <Flex justifyContent='flex-end'>
                <HeaderMenu
                  color={navigationLinkFontColor}
                  showStoreInfoLink={showStoreInfoLink}
                  locales={locales}
                  locale={locale}
                  enableReservationsOnShop={metaState?.channelEnabled?.reserve}
                />
                <UserButton
                  locale={locale}
                  user={userState}
                  buttonColor={navigationLinkFontColor}
                  buttonTextColor={headerBackgroundColor}
                  initializedOPW={initializedOPW}
                />
              </Flex>
            </NavContainer>
            <DesktopOnly>
              <CartDrawerButtonContainer
                data-testid='cartIconButton'
                itemsCount={itemsCount}
                iconSize={iconSizes.extraLarge}
              >
                <div onClick={() => modalController.showModal('CartDrawer')}>
                  <Badge
                    Icon={IconNewBag}
                    withAnimation
                    badgeBackgroundColor={navigationLinkFontColor}
                    badgeTextColor={headerBackgroundColor}
                    iconFillColor={navigationLinkFontColor}
                    iconSize={iconSizes.extraLarge}
                    value={itemsCount}
                  />
                </div>
              </CartDrawerButtonContainer>
            </DesktopOnly>
            <DesktopHidden>
              <Flex alignItems='center' flexDirection='row'>
                {metaState?.channelEnabled?.reserve && (
                  <NextLink
                    href='/[locale]/reservations'
                    as={`/${locale}/reservations`}
                    passHref
                  >
                    <LinkText color={navigationLinkFontColor}>
                      {t('Reserve')}
                    </LinkText>
                  </NextLink>
                )}
                <Flex
                  onClick={() => {
                    const action = () => {
                      if (isAppointmentReady) {
                        modalController.showModal('CartDrawer')
                      } else {
                        setTimeout(() => {
                          const navigateToProductSection =
                            document.getElementById('product-section-mobile')
                          navigateToProductSection &&
                            navigateToProductSection.scrollIntoView(true)
                        }, 100)
                      }
                    }
                    if (router.pathname === '/[locale]') {
                      action()
                      return
                    }
                    Router.push('/[locale]', `/${locale}`).then(() => action)
                  }}
                >
                  <LinkText color={navigationLinkFontColor}>
                    {isAppointmentReady ? t('Cart') : t('Order')}
                  </LinkText>
                  {itemsCount > 0 ? (
                    <StyledBadge
                      background={headerSettings?.navigationLinkFontColor}
                    >
                      <StyledText
                        bold
                        color={headerSettings?.headerBackgroundColor}
                      >
                        {itemsCount}
                      </StyledText>
                    </StyledBadge>
                  ) : null}
                </Flex>
                <Hamburger
                  fill={navigationLinkFontColor}
                  onClick={() =>
                    modalController.showModal('MobileSideNavigation', {
                      menuName: name,
                      src,
                      srcSet,
                      showStoreName,
                      showLogo,
                      sectionId,
                      createUrl
                    })
                  }
                />
              </Flex>
            </DesktopHidden>
          </Flex>
        </Flex>
      </StyledResponsiveSectionContainer>
    </StyledResponsiveSection>
  )
}

export default Header
